import { Injectable } from '@angular/core';

export type CookieScope = 'essential' | 'analytics' | 'marketing' | 'preferences';

type ConsentRecord = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
};

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private storageKey = 'cookieConsent';

  // Cookie names that are considered essential and should never be removed
  private essentialCookies = new Set(['session', 'csrftoken', 'SID']);

  // Map known non-essential cookie names to their scope. Populate as app-specific cookies are identified.
  // Example: { 'ga': 'analytics', 'fbp': 'marketing' }
  private nonEssentialCookieScope: Record<string, CookieScope> = {};

  constructor() {
    // enforce consent state on service initialization
    try {
      this.enforceConsent();
    } catch {
      // fail safe - never throw during app bootstrap
    }
  }

  getConsent(): ConsentRecord {
    const defaults: ConsentRecord = { essential: true, analytics: false, marketing: false, preferences: false };
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return defaults;
      const parsed = JSON.parse(raw || '{}');
      return {
        essential: !!parsed.essential,
        analytics: !!parsed.analytics,
        marketing: !!parsed.marketing,
        preferences: !!parsed.preferences,
      };
    } catch {
      return defaults;
    }
  }

  hasConsent(scope: CookieScope): boolean {
    const c = this.getConsent();
    return !!c[scope] || scope === 'essential';
  }

  setConsent(choices: Record<string, boolean>) {
    const current = this.getConsent();
    const merged = { ...current, ...choices };
    localStorage.setItem(this.storageKey, JSON.stringify(merged));
  }

  acceptAll() {
    this.setConsent({ analytics: true, marketing: true, preferences: true });
  }

  clearConsent() {
    localStorage.removeItem(this.storageKey);
  }

  // Remove any non-essential cookies if consent for their scope is not given
  enforceConsent(): void {
    const consent = this.getConsent();
    // Iterate cookies and remove those whose specific scope is not consented.
    const cookies = document.cookie ? document.cookie.split(';') : [];
    for (const raw of cookies) {
      const parts = raw.split('=');
      const name = parts[0].trim();
      if (!name) continue;
      if (this.essentialCookies.has(name)) continue;

      // Determine scope for this cookie name; default to 'analytics' for unknown non-essential cookies.
      const scope = this.nonEssentialCookieScope[name] ?? 'analytics';

      if (!this.hasConsent(scope)) {
        // delete cookie by setting expiry in past
        document.cookie = `${encodeURIComponent(name)}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax;${location.protocol==='https:'? 'Secure;': ''}`;
      }
    }
  }
}
